import {
  ANNOTATION_SOURCE_LOCATION,
  parseEntityRef,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { safeExternalUrl } from './safeUrl';

/**
 * Which catalog entity is the genuine entry point of a project, for the
 * shared projects list (`projectList.ts`) that the sidebar block
 * (components/projectsNav/ProjectsNav.tsx), the home page and the breadcrumb of the
 * branch page (BranchPage.tsx) read — one definition for all, since step 6
 * (2026-10-20) in `components/shared/projectIdentity.ts`, re-exported here so
 * that the existing imports keep working. See its header for the rule.
 */
export {
  PROJECT_ENTITY_FIELDS,
  PROJECT_LABEL,
  projectIdOf,
  projectOwnerRef,
} from '../shared';

/** Who created a project, and when — stamped by the Odoo project template. */
export const CREATED_BY_ANNOTATION = 'waadoo.ovh/created-by';
export const CREATED_AT_ANNOTATION = 'waadoo.ovh/created-at';

export interface ProjectCreation {
  /** A user entity ref, e.g. `user:default/dev1`. */
  createdBy?: string;
  /** An ISO date-time. */
  createdAt?: string;
}

/**
 * The creator and creation date of a project, from the annotations its
 * template wrote (`waadoo.ovh/created-by: ${{ user.ref }}`,
 * `waadoo.ovh/created-at: ${{ now() }}`). Each is kept only when it reads as
 * what it claims — a USER ref, a valid date — and left out otherwise: projects
 * created before 2026-10-04 carry neither, and show nothing. Display only.
 */
export function projectCreation(entity: Entity): ProjectCreation | undefined {
  const annotations = entity.metadata.annotations ?? {};
  const creation: ProjectCreation = {};

  const by = annotations[CREATED_BY_ANNOTATION];
  if (by) {
    try {
      const ref = parseEntityRef(by, { defaultKind: 'user' });
      if (ref.kind.toLocaleLowerCase('en-US') === 'user') {
        creation.createdBy = stringifyEntityRef(ref);
      }
    } catch {
      // Not an entity ref: left out.
    }
  }

  const at = annotations[CREATED_AT_ANNOTATION];
  if (at && !Number.isNaN(Date.parse(at))) {
    creation.createdAt = new Date(at).toISOString();
  }

  return creation.createdBy || creation.createdAt ? creation : undefined;
}

/** How the catalog writes a location of type `url` (`stringifyLocationRef`). */
const URL_LOCATION_PREFIX = 'url:';

/**
 * The web address of the repository a project's entity was read from, from
 * the `backstage.io/source-location` annotation the catalog adds to entities
 * registered from a URL — for a GitLab file, a folder URL such as
 * `url:https://<gitlab>/<group>/apps/<project>/-/tree/dev/` — keeping the part
 * before GitLab's `/-/`. Undefined when the annotation is missing,
 * is not a `url:` location, or is not an http(s) address. Display only.
 */
export function projectRepositoryUrl(entity: Entity): string | undefined {
  const location = entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION];
  if (!location?.startsWith(URL_LOCATION_PREFIX)) {
    return undefined;
  }
  const url = safeExternalUrl(location.slice(URL_LOCATION_PREFIX.length));
  if (!url) {
    return undefined;
  }
  const [repository] = url.split('/-/');
  return repository.replace(/\/+$/, '');
}
